import type { ReactNode } from 'react';
import { BiUser } from 'react-icons/bi';

import { useAuthorizedFileUrl } from '@/hooks/useAuthorizedFileUrl';

import s from './Avatar.module.scss';

export interface AvatarProps {
  src?: null | string;
  firstName?: string;
  lastName?: string;
  size?: number;
  alt?: string;
  className?: string;
  /** The glyph drawn when there is neither a picture nor a name — a person's silhouette
      unless the record is something else (a vessel kept anonymous, say). */
  icon?: ReactNode;
  /** `src` needs no sign-in (an invitation's host avatar): shown as it is, never fetched with the
      session, which cancels requests made without a vessel. */
  isPublic?: boolean;
}

export const Avatar = ({
  src,
  firstName,
  lastName,
  size = 58,
  alt = 'Avatar',
  className,
  icon,
  isPublic = false,
}: AvatarProps) => {
  const getInitials = () => {
    const firstInitial = firstName?.charAt(0).toUpperCase() || '';
    const lastInitial = lastName?.charAt(0).toUpperCase() || '';
    return `${firstInitial}${lastInitial}`;
  };

  const initials = getInitials();

  // Private backend files (e.g. chat room images) need the bearer token, which
  // a plain <img src> can't attach → 403. The hook resolves those to a blob:
  // URL and passes everything else through untouched.
  const { url: authorizedSrc } = useAuthorizedFileUrl(isPublic ? null : src);
  const resolvedSrc = isPublic ? src : authorizedSrc;

  const style = {
    width: `${size}px`,
    height: `${size}px`,
    fontSize: `${size * 0.4}px`,
  };

  if (resolvedSrc) {
    return (
      <img src={resolvedSrc} alt={alt} className={`${s.avatar} ${className || ''}`} style={style} />
    );
  }

  if (initials) {
    return (
      <div className={`${s.avatarInitials} ${className || ''}`} style={style}>
        {initials}
      </div>
    );
  }

  return (
    <div className={`${s.avatarPlaceholder} ${className || ''}`} style={style}>
      {icon ? (
        <span className={s.placeholderIcon} style={{ fontSize: size * 0.55 }} aria-hidden="true">
          {icon}
        </span>
      ) : (
        <BiUser size={size * 0.55} color="var(--color-slate-80)" />
      )}
    </div>
  );
};
